import type { TFunction } from "i18next";
import type { PreparedWorkflow } from "../../../../api/content/stepPlans";
import { typesText } from "../../../workflows/inputCards/typeWords";
import { namesText, reasonText } from "../../stepPlans/planText";

/** Why the prepared start can't begin yet, as the start form asks for it (F101). */
export interface StartBlockers {
  /** The Thread still needs the entries it works on: asked at the documents field. */
  readonly entries: boolean;
  /** One sentence naming everything still missing and any value that doesn't fit; `null` when nothing. */
  readonly text: string | null;
}

/**
 * The server's reasons the start isn't ready (ADR-0032 D7), in the start form's words: a
 * missing context input the subject binding fills is the entries, a missing value is named
 * by its input's label, and anything else is the reason's own sentence. Nothing here decides
 * what is required.
 */
export function startBlockers(prepared: PreparedWorkflow, t: TFunction): StartBlockers {
  const exposed = new Map(prepared.inputs.map((input) => [input.name, input]));
  const asked = new Map(prepared.asked.map((input) => [input.destination, input]));
  const labelOf = (name: string) => exposed.get(name)?.label ?? asked.get(name)?.label ?? name;
  let entries = false;
  const needed: string[] = [];
  const problems: string[] = [];
  for (const reason of prepared.reasons) {
    const input = reason.input === null ? null : exposed.get(reason.input);
    if (reason.code === "missing_context" && input?.binding === "subject") entries = true;
    else if (reason.code === "missing_context") needed.push(typesText(reason.accepts, t));
    else if (reason.code === "missing_input" && reason.input !== null) needed.push(t("threads.prepare.run.quoted", { input: labelOf(reason.input) }));
    else problems.push(`${reasonText(reason, t, labelOf)}.`);
  }
  const names = [...(entries ? [t("threads.prepare.run.missingEntry")] : []), ...needed];
  const sentences = [...(names.length === 0 ? [] : [t("threads.prepare.run.missing", { items: namesText(names, t) })]), ...problems];
  return { entries, text: sentences.length === 0 ? null : sentences.join(" ") };
}
